/**
 * Inbox list query. Auto-refetches on focus and runs once on mount.
 *
 * We deliberately don't use `useInfiniteQuery` for V1 — the success metric
 * is 150 WAU, the median user will have <50 threads, and a single page of
 * 100 covers everyone. When that breaks, swap to infinite without touching
 * the UI (the row list stays a flat array).
 */
import { useQuery } from "@tanstack/react-query";
import { getInbox } from "../lib/api";
import { inboxKey } from "./queryKeys";
import type { InboxResponse } from "../types/api";

export function useInbox(enabled: boolean): {
  data: InboxResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const q = useQuery<InboxResponse, Error>({
    queryKey: inboxKey(),
    queryFn: ({ signal }) => getInbox({ limit: 100 }, signal),
    enabled,
    staleTime: 4_000,
    refetchOnWindowFocus: true,
    // Polling replaces SSE since Workers can't hold a Postgres LISTEN socket.
    // Only poll while the document is visible to avoid burning quota on bg tabs.
    refetchInterval: (q) => (document.visibilityState === "visible" && q.state.data ? 5_000 : false),
    refetchIntervalInBackground: false,
  });
  return {
    data: q.data,
    isLoading: q.isLoading,
    isError: q.isError,
    refetch: q.refetch,
  };
}
